import { useEffect, useState } from "react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Plus, Trash, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { fmtEur } from "../lib/utils";

export default function AdminSimulate() {
  const { t } = useI18n();
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [items, setItems] = useState([{ product_code: "", quantity: 1 }]);
  const [country, setCountry] = useState("IT");
  const [pl, setPl] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/admin/clients").then(({ data }) => setClients(data)); }, []);
  useEffect(() => {
    if (!clientId) { setPl(null); return; }
    api.get(`/admin/pricelists/${clientId}`).then(({ data }) => setPl(data));
  }, [clientId]);

  const add = () => setItems([...items, { product_code: "", quantity: 1 }]);
  const upd = (i, k, v) => setItems(items.map((it, idx) => idx === i ? { ...it, [k]: k === "quantity" ? parseInt(v) || 1 : v } : it));
  const del = (i) => setItems(items.filter((_, idx) => idx !== i));

  const submit = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/simulate-order", {
        client_id: clientId, items, shipping_country: country,
      });
      setResult(data);
      if (data.blocked) toast.error("Ordine bloccato - saldo insufficiente");
      else toast.success("Ordine simulato e addebitato");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-simulate">
      <div>
        <div className="tf-label">TEST TOOL</div>
        <h1 className="font-display text-4xl font-black tracking-tight mt-1">{t("simulate_order")}</h1>
        <p className="text-sm text-[var(--tf-muted)] mt-2">Simula un ordine Myze per testare calcolo costi e scaling wallet.</p>
      </div>

      <div className="tf-card p-6 space-y-5">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="tf-label block mb-2">{t("choose_client")}</label>
            <select value={clientId} onChange={(e) => setClientId(e.target.value)} className="tf-input" data-testid="sim-client">
              <option value="">—</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.company_name} · {fmtEur(c.wallet_balance)}</option>)}
            </select>
          </div>
          <div>
            <label className="tf-label block mb-2">{t("shipping_to")}</label>
            <select value={country} onChange={(e) => setCountry(e.target.value)} className="tf-input" data-testid="sim-country">
              <option value="IT">Italia</option>
              <option value="FR">Francia (UE)</option>
              <option value="DE">Germania (UE)</option>
              <option value="US">USA (Extra-UE)</option>
              <option value="UK">UK (Extra-UE)</option>
            </select>
          </div>
        </div>

        <div>
          <div className="tf-label mb-2">Items</div>
          <div className="space-y-2">
            {items.map((it, i) => (
              <div key={i} className="grid grid-cols-[1fr_90px_auto] gap-2 items-center">
                <select value={it.product_code} onChange={(e) => upd(i, "product_code", e.target.value)} className="tf-input min-w-0" data-testid={`sim-item-code-${i}`}>
                  <option value="">— {t("product")} —</option>
                  {pl?.items?.map((p) => <option key={p.code} value={p.code}>{p.label} ({fmtEur(p.price)})</option>)}
                </select>
                <input type="number" min="1" value={it.quantity} onChange={(e) => upd(i, "quantity", e.target.value)} className="tf-input font-mono text-center" data-testid={`sim-item-qty-${i}`} />
                <button onClick={() => del(i)} className="tf-btn tf-btn-danger !px-2 !py-2"><Trash size={14} weight="bold" /></button>
              </div>
            ))}
          </div>
          <button onClick={add} className="tf-btn tf-btn-ghost mt-3" data-testid="sim-add-item"><Plus size={14} weight="bold" />{t("add_item")}</button>
        </div>

        <button onClick={submit} disabled={!clientId || loading} className="tf-btn tf-btn-primary" data-testid="sim-submit">
          <Lightning size={16} weight="bold" />{loading ? "..." : t("simulate_order")}
        </button>
      </div>

      {result && (
        <div className={`tf-card p-6 border-2 ${result.blocked ? "border-[var(--tf-error)]" : "border-[var(--tf-success)]"}`} data-testid="sim-result">
          <div className="tf-label">{result.blocked ? "ORDINE BLOCCATO" : "ORDINE ADDEBITATO"}</div>
          <div className="grid md:grid-cols-3 gap-4 mt-4">
            <div><div className="tf-label">{t("cost")}</div><div className="font-mono text-xl">{fmtEur(result.cost.items_total)}</div></div>
            <div><div className="tf-label">{t("shipping")}</div><div className="font-mono text-xl">{fmtEur(result.cost.shipping)}</div></div>
            <div><div className="tf-label">{t("total")}</div><div className="font-mono text-2xl font-bold">{fmtEur(result.cost.total)}</div></div>
          </div>
          {!result.blocked && <div className="mt-4 text-sm">Nuovo saldo: <span className="font-mono font-bold">{fmtEur(result.balance_after)}</span></div>}
        </div>
      )}
    </div>
  );
}
